import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileCheck, FilePen, Inbox, MailPlus } from "lucide-react";
import api from "./api";
import { Badge, CATEGORY_LABELS, Spinner, StatusBadge, fmtDate, fmtDateTime } from "./ui";

const CARDS = [
    { key: "newMessages", label: "Νέα μηνύματα", icon: MailPlus, testId: "stat-new-messages", accent: "text-[#F7EAB8]" },
    { key: "totalMessages", label: "Σύνολο μηνυμάτων", icon: Inbox, testId: "stat-total-messages", accent: "text-[#4CC9DC]" },
    { key: "publishedArticles", label: "Δημοσιευμένα άρθρα", icon: FileCheck, testId: "stat-published", accent: "text-[#5FBBAC]" },
    { key: "draftArticles", label: "Πρόχειρα άρθρα", icon: FilePen, testId: "stat-drafts", accent: "text-[#A3B3B8]" },
];

export const Dashboard = () => {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/api/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
    }, []);

    if (stats === null) return <Spinner testId="dashboard-loading" />;
    if (stats === false)
        return <p className="text-sm text-red-300" data-testid="dashboard-error">Αποτυχία φόρτωσης στατιστικών.</p>;

    return (
        <div className="mx-auto max-w-6xl" data-testid="admin-dashboard">
            <h1 className="text-2xl font-bold text-[#F2F5F6]">Πίνακας ελέγχου</h1>
            <p className="mt-1 text-sm text-[#697B80]">Επισκόπηση μηνυμάτων και άρθρων του TheraOffice.</p>

            <div className="mt-8 grid grid-cols-2 gap-4 xl:grid-cols-4">
                {CARDS.map(({ key, label, icon: Icon, testId, accent }) => (
                    <div key={key} className="rounded-2xl border border-white/10 bg-[#093448] p-5" data-testid={testId}>
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#697B80]">{label}</p>
                            <Icon size={18} className={accent} />
                        </div>
                        <p className="mt-3 text-3xl font-bold text-[#F2F5F6]" data-testid={`${testId}-value`}>{stats[key]}</p>
                    </div>
                ))}
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <section className="rounded-2xl border border-white/10 bg-[#093448] p-5" data-testid="latest-messages-list">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-base font-bold text-[#F2F5F6]">Τελευταία μηνύματα</h2>
                        <Link to="/messages" className="text-xs font-bold text-[#4CC9DC] hover:text-[#F7EAB8]">Όλα →</Link>
                    </div>
                    {stats.latestMessages.length === 0 ? (
                        <p className="py-6 text-center text-sm text-[#697B80]">Δεν υπάρχουν μηνύματα ακόμη.</p>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {stats.latestMessages.map((m) => (
                                <li key={m.id} className="py-3" data-testid={`dashboard-message-${m.id}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="truncate text-sm font-semibold text-[#F2F5F6]">{m.name}</p>
                                        <StatusBadge status={m.status} />
                                    </div>
                                    <p className="mt-1 line-clamp-1 text-xs text-[#A3B3B8]">{m.message}</p>
                                    <p className="mt-1 text-[11px] text-[#697B80]">{fmtDateTime(m.created_at)}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="rounded-2xl border border-white/10 bg-[#093448] p-5" data-testid="latest-articles-list">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-base font-bold text-[#F2F5F6]">Τελευταία άρθρα</h2>
                        <Link to="/articles" className="text-xs font-bold text-[#4CC9DC] hover:text-[#F7EAB8]">Όλα →</Link>
                    </div>
                    {stats.latestArticles.length === 0 ? (
                        <p className="py-6 text-center text-sm text-[#697B80]">Δεν υπάρχουν άρθρα ακόμη.</p>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {stats.latestArticles.map((a) => (
                                <li key={a.id} className="py-3" data-testid={`dashboard-article-${a.id}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <Link to={`/articles/${a.id}`} className="truncate text-sm font-semibold text-[#F2F5F6] hover:text-[#4CC9DC]">
                                            {a.title_el}
                                        </Link>
                                        <Badge testId={`article-status-${a.status}`} className={a.status === "published" ? "border-[#5FBBAC]/40 bg-[#5FBBAC]/10 text-[#5FBBAC]" : "border-[#F7EAB8]/40 bg-[#F7EAB8]/10 text-[#F7EAB8]"}>
                                            {a.status === "published" ? "Δημοσιευμένο" : "Πρόχειρο"}
                                        </Badge>
                                    </div>
                                    <p className="mt-1 text-[11px] text-[#697B80]">
                                        {CATEGORY_LABELS[a.category] || a.category} · {fmtDate(a.published_at)}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
};
